import IndexForm from '@/components/pages/create-webinar/waiting-room/IndexForm';
import WaitingRoomBackground from '@/components/pages/create-webinar/waiting-room/WaitingRoomBackground';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  deleteWaitingRoomPageMainAsset,
  fetchWaitingRoomPage,
  fetchWebinarIndex,
  listQuizTemplates,
  listSpeakers,
  listSurveyTemplates,
  updateWaitingRoomPage,
  uploadWaitingRoomPageBackgroundImage,
  uploadWaitingRoomPageImage,
  uploadWaitingRoomPageVideo,
} from '@/features/webinar/webinarThunks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '@/lib/evergreenMp4File';
import SurveyFormWaitingRoom from '@/components/pages/create-webinar/waiting-room/SurveyFormWaitingRoom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import QuizFormWaitingRoom from '@/components/pages/create-webinar/waiting-room/QuizFormWaitingRoom';
import type { WaitingRoomInteractionType } from '@/features/webinar/types';
import Loader from '@/components/ui/Loader';
import { Button } from '@/components/ui/button';
import { toast } from 'react-toastify';
import { mapHttpError } from '@/lib/mapApiError';
import type { AppError } from '@/features/auth/types';
import { Trash2 } from 'lucide-react';
import { InputLabel } from '@/components/ui/InputLabel';

const WAITING_ROOM_SEG = '/waiting-room/';

/** Nom fichier média salle d’attente depuis URL ou chemin (gère %2F dans un seul segment). */
function mediaDisplayNameFromRef(ref: string | null | undefined, fallback: string): string {
  if (!ref?.trim()) {
    return fallback;
  }

  let pathname: string;
  try {
    pathname = new URL(
      ref,
      typeof globalThis !== 'undefined' ? globalThis.location.href : 'https://local',
    ).pathname;
  } catch {
    pathname = ref.split(/[?#]/)[0] ?? ref;
  }

  const nameAfterWaitingRoom = (raw: string): string | null => {
    let p = raw;
    if (p.includes('%')) {
      try {
        p = decodeURIComponent(p);
      } catch {
        p = p.replace(/%2F/gi, '/');
      }
    }
    const i = p.indexOf(WAITING_ROOM_SEG);
    if (i === -1) {
      return null;
    }
    const last = p
      .slice(i + WAITING_ROOM_SEG.length)
      .split('/')
      .filter(Boolean)
      .at(-1);
    if (!last) {
      return null;
    }
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  };

  const tail = pathname.split('/').filter(Boolean).at(-1) ?? '';
  const fromWaitingRoom = nameAfterWaitingRoom(pathname) ?? nameAfterWaitingRoom(tail);
  if (fromWaitingRoom) {
    return fromWaitingRoom;
  }
  if (!tail) {
    return fallback;
  }
  try {
    return decodeURIComponent(tail);
  } catch {
    return tail;
  }
}

export type WaitingRoomSectionProps = {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
};

export type WaitingRoomFormValues = {
  showLogo: boolean;
  showSpeakers: boolean;
  showGuests: boolean;
  showIndex: boolean;
  displayParticipantsCountEnabled: boolean;
  displayParticipantsMinThreshold: number;
  background: 'DEFAULT' | 'GRADIENT' | 'IMAGE';
  backgroundColor: string | null;
  backgroundGradient: string | null;
  interactionType: WaitingRoomInteractionType;
};

export default function WaitingRoomSection({
  currentWebinarId,
  isEdit,
  onSaveSuccess,
}: WaitingRoomSectionProps) {
  const { t } = useTranslation('webinars');
  const { t: tCommon, i18n } = useTranslation('common');
  const { t: tCW } = useTranslation('createWebinar');

  const waitingRoomFormSchema = useMemo(
    () =>
      z.object({
        showLogo: z.boolean(),
        showSpeakers: z.boolean(),
        showGuests: z.boolean(),
        showIndex: z.boolean(),
        background: z.enum(['DEFAULT', 'GRADIENT', 'IMAGE'], {
          message: t('waitingRoom.errors.selectBackgroundType'),
        }),
        backgroundColor: z.string().nullable(),
        backgroundGradient: z.string().nullable(),
        interactionType: z.enum(['SURVEY', 'QUIZ', 'NONE'], {
          message: t('waitingRoom.errors.selectInteractionType'),
        }),
        displayParticipantsCountEnabled: z.boolean(),
        displayParticipantsMinThreshold: z.number().min(0),
      }),
    [t],
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);

  const [showPreWebinarActivity, setShowPreWebinarActivity] = useState(false);
  const [quizForm, setQuizForm] = useState<UseFormReturn<any> | undefined>(undefined);
  const [surveyForm, setSurveyForm] = useState<UseFormReturn<any> | undefined>(undefined);

  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const {
    currentWaitingRoomPage,
    waitingRoomPageLoading,
    currentWebinarIndex,
    quizTemplates,
    surveyTemplates,
    indexLoading,
    currentSessionConfiguration,
    currentWebinar,
    speakers,
  } = useAppSelector((state) => state.webinar);
  const isEvergreen = currentWebinar?.type === 'EVERGREEN';

  const [selectedActivity, setSelectedActivity] = useState<WaitingRoomInteractionType>(
    currentWaitingRoomPage?.interactionType || 'NONE',
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [selectedBackgroundImageFile, setSelectedBackgroundImageFile] = useState<File | null>(null);
  const [backgroundImagePreviewUrl, setBackgroundImagePreviewUrl] = useState<string | null>(null);
  const [mainImageCleared, setMainImageCleared] = useState(false);
  const [mainVideoCleared, setMainVideoCleared] = useState(false);
  // Toggle explicite « afficher un média » : ON par défaut uniquement si un média est déjà configuré
  const [showMainMedia, setShowMainMedia] = useState(false);
  const [showImageReplacesVideoNote, setShowImageReplacesVideoNote] = useState(false);
  const [showVideoReplacesImageNote, setShowVideoReplacesImageNote] = useState(false);
  const originalMainImageUrlRef = useRef<string | null>(null);
  const originalMainVideoUrlRef = useRef<string | null>(null);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = currentWebinarId || webinarIdParam;

  // Le toggle « afficher les invités » n'a de sens que si le webinaire a au moins un invité
  const hasGuests = useMemo(() => {
    const id = typeof webinarId === 'number' ? webinarId : parseInt(String(webinarId), 10);
    return speakers.some((s) => s.webinarId === id);
  }, [speakers, webinarId]);

  const mainMediaServerFingerprint = useMemo(
    () =>
      currentWaitingRoomPage
        ? [
            currentWaitingRoomPage.webinarId,
            currentWaitingRoomPage.mainAssetType,
            currentWaitingRoomPage.mainImage ?? '',
            currentWaitingRoomPage.mainImageUrl ?? '',
            currentWaitingRoomPage.mainVideo ?? '',
            currentWaitingRoomPage.mainVideoUrl ?? '',
          ].join('\0')
        : '',
    [currentWaitingRoomPage],
  );

  useEffect(() => {
    setMainImageCleared(false);
    setMainVideoCleared(false);
    setShowImageReplacesVideoNote(false);
    setShowVideoReplacesImageNote(false);
  }, [mainMediaServerFingerprint]);

  // Le toggle reflète displayMainMedia (true par défaut pour la rétrocompat)
  useEffect(() => {
    if (!currentWaitingRoomPage) {
      return;
    }
    setShowMainMedia(currentWaitingRoomPage.displayMainMedia !== false);
  }, [mainMediaServerFingerprint, currentWaitingRoomPage]);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setSelectedFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setSelectedFileUrl(null);
      };
    } else {
      setSelectedFileUrl(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (selectedVideo) {
      const url = URL.createObjectURL(selectedVideo);
      setSelectedVideoUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setSelectedVideoUrl(null);
      };
    } else {
      setSelectedVideoUrl(null);
    }
  }, [selectedVideo]);

  useEffect(() => {
    if (selectedBackgroundImageFile) {
      const url = URL.createObjectURL(selectedBackgroundImageFile);
      setBackgroundImagePreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setBackgroundImagePreviewUrl(null);
      };
    } else {
      setBackgroundImagePreviewUrl(null);
    }
  }, [selectedBackgroundImageFile]);

  // Le fichier sélectionné localement n'a plus lieu d'être une fois la page rechargée
  // depuis le serveur (sauvegarde réussie ou navigation).
  useEffect(() => {
    setSelectedBackgroundImageFile(null);
  }, [currentWaitingRoomPage?.backgroundImageUrl]);
  const form = useForm<WaitingRoomFormValues>({
    resolver: zodResolver(waitingRoomFormSchema),
    defaultValues: {
      showLogo: false,
      showSpeakers: false,
      showGuests: false,
      showIndex: false,
      displayParticipantsCountEnabled: false,
      displayParticipantsMinThreshold: 0,
      background: 'DEFAULT',
      backgroundColor: visualIdentity?.backgroundColor || '#FBFBFB',
      backgroundGradient: '1',
      interactionType: 'NONE',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    const currentGradient = form.getValues('backgroundGradient');
    const currentBackground = form.getValues('background');

    if (currentGradient === undefined) {
      if (currentBackground === 'GRADIENT') {
        form.setValue('backgroundGradient', '1', { shouldValidate: false });
      } else {
        form.setValue('backgroundGradient', null, { shouldValidate: false });
      }
    }
  }, [form]);

  useEffect(() => {
    const currentBackgroundColor = form.getValues('backgroundColor');
    const currentBackground = form.getValues('background');

    if (currentBackgroundColor === undefined) {
      if (currentBackground === 'DEFAULT') {
        const fallbackColor =
          visualIdentity?.backgroundColor && visualIdentity.backgroundColor.trim().length > 0
            ? visualIdentity.backgroundColor
            : '#FBFBFB';
        form.setValue('backgroundColor', fallbackColor, { shouldValidate: false });
      } else {
        form.setValue('backgroundColor', null, { shouldValidate: false });
      }
    }
  }, [form, visualIdentity]);

  useEffect(() => {
    console.log('Form state changed:', {
      isValid: form.formState.isValid,
      isSubmitting: form.formState.isSubmitting,
      errors: form.formState.errors,
      values: form.getValues(),
    });
  }, [form.formState.isValid, form.formState.isSubmitting, form.formState.errors, form]);

  const onSubmit = useCallback(
    async (data: WaitingRoomFormValues, backgroundModified?: boolean) => {
      console.log('data', data);

      if (!workspaceId || !webinarId) {
        return;
      }

      const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
      if (isNaN(id)) {
        return;
      }

      if (
        showMainMedia &&
        !selectedFile &&
        !selectedVideo &&
        !serverImageActive &&
        !serverVideoActive
      ) {
        toast.error(t('waitingRoom.mainAssetRequiredError'));
        return;
      }

      try {
        let mainAssetTypeUpdate: 'IMAGE' | 'VIDEO' | undefined;

        if (selectedFile) {
          await dispatch(
            uploadWaitingRoomPageImage({
              workspaceId,
              webinarId: id,
              file: selectedFile,
            }),
          ).unwrap();
          setSelectedFile(null);
          mainAssetTypeUpdate = 'IMAGE';
        } else if (selectedVideo) {
          await dispatch(
            uploadWaitingRoomPageVideo({
              workspaceId,
              webinarId: id,
              file: selectedVideo,
            }),
          ).unwrap();
          setSelectedVideo(null);
          mainAssetTypeUpdate = 'VIDEO';
        }

        await dispatch(
          updateWaitingRoomPage({
            workspaceId,
            webinarId: id,
            input: {
              displayLogo: data.showLogo,
              displaySpeakers: data.showSpeakers,
              displayGuests: data.showGuests,
              displayIndex: data.showIndex,
              displayParticipantsCountEnabled: data.displayParticipantsCountEnabled,
              displayParticipantsMinThreshold: data.displayParticipantsMinThreshold,
              background: data.background,
              backgroundColor: data.backgroundColor,
              backgroundGradient: data.backgroundGradient,
              interactionType: data.interactionType,
              displayMainMedia: showMainMedia,
              ...(mainAssetTypeUpdate !== undefined ? { mainAssetType: mainAssetTypeUpdate } : {}),
              ...(backgroundModified !== undefined && { backgroundModified }),
            },
          }),
        ).unwrap();

        /** L'upload du fichier se fait après la sauvegarde de `background: 'IMAGE'`, jamais au
         * moment de la sélection — sinon un échec silencieux laisse l'organisateur sans image
         * et sans indication (même correctif que la page d'inscription). */
        if (data.background === 'IMAGE' && selectedBackgroundImageFile) {
          await dispatch(
            uploadWaitingRoomPageBackgroundImage({
              workspaceId,
              webinarId: id,
              file: selectedBackgroundImageFile,
            }),
          ).unwrap();
          setSelectedBackgroundImageFile(null);
        }

        setMainImageCleared(false);
        setMainVideoCleared(false);
        setShowImageReplacesVideoNote(false);
        setShowVideoReplacesImageNote(false);
        if (!isEdit) {
          if (currentSessionConfiguration?.type === 'EVERGREEN') {
            navigate(`/create-webinar/evergreen/${id}`);
          } else {
            navigate(`/create-webinar/live/${id}`);
          }
        } else if (isEdit && onSaveSuccess) {
          onSaveSuccess();
        }

        toast.success(t('waitingRoom.saveSuccess'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        console.error('Failed to save waiting room page', error);
        const appError = error as AppError | undefined;
        const errorMessage = appError?.message ? mapHttpError(appError.message) : 'errors.generic';
        const translatedMessage = tCommon(
          errorMessage,
          'Une erreur est survenue lors de la sauvegarde.',
        );
        toast.error(translatedMessage);

        if (appError?.details && Array.isArray(appError.details)) {
          appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
            if (detail.message) {
              const mappedMessage = mapHttpError(detail.message);
              const translatedDetailMessage = tCommon(mappedMessage, detail.message);
              toast.error(translatedDetailMessage);
            }
          });
        }
      }
    },
    [
      selectedFile,
      selectedVideo,
      selectedBackgroundImageFile,

      workspaceId,

      webinarId,

      dispatch,

      navigate,

      isEdit,

      onSaveSuccess,

      t,

      tCommon,
      ,
      currentSessionConfiguration,
    ],
  );

  const getDefaultBackgroundColor = useCallback(() => {
    const currentColor = form.getValues('backgroundColor');
    if (currentColor && currentColor.trim().length > 0) {
      return currentColor;
    }

    if (visualIdentity?.backgroundColor && visualIdentity.backgroundColor.trim().length > 0) {
      return visualIdentity.backgroundColor;
    }

    return '#FBFBFB';
  }, [form, visualIdentity]);

  useEffect(() => {
    if (webinarId && workspaceId) {
      const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
      if (!isNaN(id)) {
        // dispatch(fetchWebinarById({ workspaceId, id }));
        dispatch(fetchWaitingRoomPage({ workspaceId, webinarId: id }));
        dispatch(listSpeakers({ workspaceId, webinarId: id, page: 0, size: 100 }));
        dispatch(listQuizTemplates({ workspaceId, webinarId: id, page: 0, size: 100 }));
        dispatch(listSurveyTemplates({ workspaceId, webinarId: id, page: 0, size: 100 }));
        dispatch(fetchWebinarIndex({ workspaceId, webinarId: id }));
      }
    }
  }, [webinarId, workspaceId, dispatch]);

  // L'identité visuelle ne sert qu'à la couleur de repli : lue via une ref pour qu'un chargement
  // tardif ne remette pas le formulaire à zéro sous l'utilisateur.
  const visualIdentityRef = useRef(visualIdentity);
  useEffect(() => {
    visualIdentityRef.current = visualIdentity;
  }, [visualIdentity]);

  useEffect(() => {
    if (currentWaitingRoomPage) {
      const fallbackBackgroundColor = visualIdentityRef.current?.backgroundColor;
      let backgroundColorValue: string | null = null;
      if (currentWaitingRoomPage.background === 'DEFAULT') {
        if (
          currentWaitingRoomPage.backgroundColor &&
          currentWaitingRoomPage.backgroundColor.trim().length > 0
        ) {
          backgroundColorValue = currentWaitingRoomPage.backgroundColor;
        } else {
          backgroundColorValue =
            fallbackBackgroundColor && fallbackBackgroundColor.trim().length > 0
              ? fallbackBackgroundColor
              : '#FBFBFB';
        }
      } else {
        backgroundColorValue = null;
      }

      originalMainImageUrlRef.current =
        currentWaitingRoomPage.mainImage || currentWaitingRoomPage.mainImageUrl || null;

      originalMainVideoUrlRef.current =
        currentWaitingRoomPage.mainVideo || currentWaitingRoomPage.mainVideoUrl || null;

      form.reset({
        showLogo: currentWaitingRoomPage.displayLogo,
        showSpeakers: currentWaitingRoomPage.displaySpeakers,
        showGuests: currentWaitingRoomPage.displayGuests,
        showIndex: currentWaitingRoomPage.displayIndex,
        displayParticipantsCountEnabled: currentWaitingRoomPage.displayParticipantsCountEnabled,
        displayParticipantsMinThreshold: currentWaitingRoomPage.displayParticipantsMinThreshold,
        background: currentWaitingRoomPage.background,
        backgroundColor: backgroundColorValue,
        backgroundGradient:
          currentWaitingRoomPage.background === 'GRADIENT'
            ? currentWaitingRoomPage.backgroundGradient || '1'
            : null,
        interactionType: currentWaitingRoomPage.interactionType,
      });
      setSelectedActivity(currentWaitingRoomPage.interactionType);

      if (
        currentWaitingRoomPage.background === 'GRADIENT' &&
        !currentWaitingRoomPage.backgroundGradient
      ) {
        form.setValue('backgroundGradient', '1');
      }
    }
  }, [currentWaitingRoomPage, form]);

  const watchedInteractionType = form.watch('interactionType');

  useEffect(() => {
    if (!webinarId || !quizTemplates || !surveyTemplates) {
      return;
    }

    const webinarIdNum = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
    if (isNaN(webinarIdNum)) {
      return;
    }

    const hasWaitingRoomQuiz = quizTemplates.some(
      (quiz) =>
        quiz.webinarId === webinarIdNum &&
        quiz.enabledDuringLive === false &&
        quiz.displayWaitingRoomQuiz === true,
    );

    const hasWaitingRoomSurvey = surveyTemplates.some(
      (survey) =>
        survey.webinarId === webinarIdNum &&
        survey.waitingRoomSurvey === true &&
        survey.displayWaitingRoomSurvey === true,
    );

    if (hasWaitingRoomQuiz || hasWaitingRoomSurvey) {
      if (watchedInteractionType !== 'NONE') {
        setShowPreWebinarActivity(true);
      }
    }
  }, [webinarId, quizTemplates, surveyTemplates, watchedInteractionType]);

  const handleSwitchChange = useCallback(
    (
      field:
        | 'showLogo'
        | 'showSpeakers'
        | 'showGuests'
        | 'showIndex'
        | 'displayParticipantsCountEnabled',
    ) =>
      (checked: boolean) => {
        form.setValue(field, checked, { shouldDirty: true });
      },
    [form],
  );

  const handleBackgroundChange = useCallback(
    (value: WaitingRoomFormValues['background']) => {
      form.setValue('background', value, { shouldDirty: true });
      if (value === 'GRADIENT') {
        const gradientValue = form.getValues('backgroundGradient') ?? '1';
        form.setValue('backgroundGradient', gradientValue, { shouldDirty: true });
        form.setValue('backgroundColor', null, { shouldDirty: true });
      } else if (value === 'DEFAULT') {
        const fallbackColor = getDefaultBackgroundColor();
        form.setValue('backgroundGradient', null, { shouldDirty: true });
        form.setValue('backgroundColor', fallbackColor, { shouldDirty: true });
      } else {
        form.setValue('backgroundGradient', null, { shouldDirty: true });
        form.setValue('backgroundColor', null, { shouldDirty: true });
      }
    },
    [form, getDefaultBackgroundColor],
  );

  const handleGradientChange = useCallback(
    (gradientValue: string) => {
      form.setValue('backgroundGradient', gradientValue, { shouldDirty: true });
      form.setValue('backgroundColor', null, { shouldDirty: true });
    },
    [form],
  );

  const removeServerMainMedia = useCallback(async () => {
    if (!workspaceId || !webinarId) {
      return;
    }
    const id = typeof webinarId === 'number' ? webinarId : parseInt(String(webinarId), 10);
    if (Number.isNaN(id)) {
      return;
    }
    try {
      await dispatch(deleteWaitingRoomPageMainAsset({ workspaceId, webinarId: id })).unwrap();
      setMainImageCleared(false);
      setMainVideoCleared(false);
      setShowImageReplacesVideoNote(false);
      setShowVideoReplacesImageNote(false);
      toast.success(t('waitingRoom.mainAssetRemoved'));
    } catch (error) {
      console.error('Failed to delete waiting room main asset', error);
      const appError = error as AppError | undefined;
      const errorMessage = appError?.message ? mapHttpError(appError.message) : 'errors.generic';
      toast.error(tCommon(errorMessage, t('waitingRoom.mainAssetRemoveError')));
    }
  }, [workspaceId, webinarId, dispatch, t, tCommon]);

  const handleBackgroundImageSelect = useCallback((file: File) => {
    setSelectedBackgroundImageFile(file);
  }, []);

  const serverImageActive = useMemo(() => {
    if (!currentWaitingRoomPage || mainImageCleared) {
      return false;
    }
    if (currentWaitingRoomPage.mainAssetType === 'VIDEO') {
      return false;
    }
    return Boolean(
      currentWaitingRoomPage.mainImage?.trim() || currentWaitingRoomPage.mainImageUrl?.trim(),
    );
  }, [currentWaitingRoomPage, mainImageCleared]);

  const serverVideoActive = useMemo(() => {
    if (!currentWaitingRoomPage || mainVideoCleared) {
      return false;
    }
    if (currentWaitingRoomPage.mainAssetType !== 'VIDEO') {
      return false;
    }
    return Boolean(
      currentWaitingRoomPage.mainVideo?.trim() || currentWaitingRoomPage.mainVideoUrl?.trim(),
    );
  }, [currentWaitingRoomPage, mainVideoCleared]);

  const mainVideoPreviewSrc = useMemo(() => {
    if (mainVideoCleared) {
      return '';
    }
    if (selectedVideoUrl) {
      return selectedVideoUrl;
    }
    if (!selectedFileUrl && !selectedFile && currentWaitingRoomPage?.mainAssetType === 'VIDEO') {
      return currentWaitingRoomPage.mainVideoUrl || currentWaitingRoomPage.mainVideo || '';
    }
    return '';
  }, [mainVideoCleared, selectedVideoUrl, selectedFileUrl, selectedFile, currentWaitingRoomPage]);

  const mainImagePreviewSrc = useMemo(() => {
    if (mainImageCleared) {
      return '';
    }
    if (selectedFileUrl) {
      return selectedFileUrl;
    }
    if (
      !selectedVideoUrl &&
      !selectedVideo &&
      currentWaitingRoomPage &&
      currentWaitingRoomPage.mainAssetType !== 'VIDEO'
    ) {
      return currentWaitingRoomPage.mainImage || currentWaitingRoomPage.mainImageUrl || '';
    }
    return '';
  }, [mainImageCleared, selectedFileUrl, selectedVideoUrl, selectedVideo, currentWaitingRoomPage]);

  const handleToggleMainMedia = useCallback(
    (checked: boolean) => {
      setShowMainMedia(checked);
      if (!workspaceId || !webinarId) return;
      const id = typeof webinarId === 'number' ? webinarId : parseInt(String(webinarId), 10);
      if (Number.isNaN(id)) return;
      const data = form.getValues();
      void dispatch(
        updateWaitingRoomPage({
          workspaceId,
          webinarId: id,
          input: {
            displayLogo: data.showLogo,
            displaySpeakers: data.showSpeakers,
            displayGuests: data.showGuests,
            displayIndex: data.showIndex,
            displayParticipantsCountEnabled: data.displayParticipantsCountEnabled,
            displayParticipantsMinThreshold: data.displayParticipantsMinThreshold,
            background: data.background,
            backgroundColor: data.backgroundColor,
            backgroundGradient: data.backgroundGradient,
            interactionType: data.interactionType,
            displayMainMedia: checked,
          },
        }),
      );
      if (!checked) {
        setSelectedFile(null);
        setSelectedVideo(null);
        setShowImageReplacesVideoNote(false);
        setShowVideoReplacesImageNote(false);
      }
    },
    [workspaceId, webinarId, dispatch, form],
  );

  if (waitingRoomPageLoading || !currentWaitingRoomPage) {
    return (
      <TitleLayout
        title={t('waitingRoom.title')}
        subtitle={t('waitingRoom.subtitle')}
        description={t('waitingRoom.description')}
      >
        <div className="space-y-8">
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>

            <div className="flex justify-between items-center">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          </div>

          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full rounded-[20px]" />
          </div>

          <div className="flex justify-between items-center">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-6 w-11 rounded-full" />
          </div>

          <div className="space-y-4">
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-28 w-full rounded-[20px]" />
            <Skeleton className="h-28 w-full rounded-[20px]" />
          </div>

          <div className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-40 w-full rounded-[20px]" />
          </div>
        </div>
      </TitleLayout>
    );
  }

  return (
    <TitleLayout
      title={t('waitingRoom.title')}
      subtitle={t('waitingRoom.subtitle')}
      isEdit={isEdit}
      description={t('waitingRoom.description')}
    >
      <div className="space-y-8">
        <div className="space-y-8">
          {visualIdentity?.logoUrl && (
            <div className="flex justify-between items-center">
              <Label>{t('waitingRoom.displayLogo')}</Label>
              <Switch
                checked={form.watch('showLogo')}
                onCheckedChange={handleSwitchChange('showLogo')}
              />
            </div>
          )}
          <div className="flex justify-between items-center">
            <Label>{t('waitingRoom.displaySpeakers')}</Label>
            <Switch
              checked={form.watch('showSpeakers')}
              onCheckedChange={handleSwitchChange('showSpeakers')}
            />
          </div>
          {hasGuests && (
            <div className="flex justify-between items-center">
              <Label>{t('waitingRoom.displayGuests')}</Label>
              <Switch
                checked={form.watch('showGuests')}
                onCheckedChange={handleSwitchChange('showGuests')}
              />
            </div>
          )}
          <div className="flex justify-between items-center">
            <Label htmlFor="displayParticipantsCountEnabled">
              {t('waitingRoom.displayParticipantsCountEnabled')}
            </Label>
            <Switch
              id="displayParticipantsCountEnabled"
              checked={form.watch('displayParticipantsCountEnabled')}
              onCheckedChange={handleSwitchChange('displayParticipantsCountEnabled')}
            />
          </div>
          {form.watch('displayParticipantsCountEnabled') && (
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="displayParticipantsMinThreshold" className="text-secondary">
                {t('waitingRoom.displayParticipantsThreshold')}
              </Label>
              <div className="relative">
                <InputLabel
                  id="displayParticipantsMinThreshold"
                  label={t('waitingRoom.displayParticipantsThreshold')}
                  className="w-60"
                  inputMode="numeric"
                  min={0}
                  maxLength={6}
                  value={form.watch('displayParticipantsMinThreshold')?.toString() || '0'}
                  onChange={(e) =>
                    form.setValue('displayParticipantsMinThreshold', parseInt(e.target.value, 10))
                  }
                />
                <p className="absolute top-0.5 right-2 text-base translate-y-1/2 text-tertiary">
                  {t('waitingRoom.people')}
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center">
            <Label>{t('waitingRoom.displayIndex')}</Label>
            <Switch
              checked={form.watch('showIndex')}
              onCheckedChange={handleSwitchChange('showIndex')}
            />
          </div>
        </div>

        <IndexForm
          webinarId={typeof webinarId === 'number' ? webinarId : parseInt(webinarId ?? '', 10)}
          showIndex={form.watch('showIndex')}
          currentWebinarIndex={currentWebinarIndex || undefined}
          indexLoading={indexLoading}
          isEdit={isEdit}
          isEvergreen={isEvergreen}
        />
        <div className="flex flex-col gap-1">
          <div className="flex justify-between items-center">
            <Label>{t('waitingRoom.showMediaLabel')}</Label>
            <Switch checked={showMainMedia} onCheckedChange={handleToggleMainMedia} />
          </div>
          <p className="text-sm text-gray-tertiary">{t('waitingRoom.showMediaHint')}</p>
        </div>
        {showMainMedia && (
          <>
            <div className="flex flex-col md:flex-row justify-between gap-4 items-center">
              {/* Main Image */}
              <div
                className="relative border w-full border-medium rounded-2xl"
                style={{ height: 'stretch' }}
              >
                <input
                  type="file"
                  id="file-upload"
                  className="sr-only"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) {
                      return;
                    }
                    const replacesVideo =
                      selectedVideo != null ||
                      Boolean(
                        currentWaitingRoomPage &&
                        !mainVideoCleared &&
                        currentWaitingRoomPage.mainAssetType === 'VIDEO' &&
                        (currentWaitingRoomPage.mainVideo?.trim() ||
                          currentWaitingRoomPage.mainVideoUrl?.trim()),
                      );
                    setMainImageCleared(false);
                    setMainVideoCleared(false);
                    setShowVideoReplacesImageNote(false);
                    setSelectedVideo(null);
                    setSelectedFile(file);
                    setShowImageReplacesVideoNote(replacesVideo);
                  }}
                />
                <Label
                  htmlFor="file-upload"
                  className="relative p-4 h-auto bg-white rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1 pr-10">
                    <div className="size-10 shrink-0 bg-medium rounded-lg flex items-center justify-center">
                      <img
                        src="/assets//attach.svg"
                        alt={t('offersForm.content.fileInput.attachIconAlt')}
                      />
                    </div>
                    <div className="flex flex-col gap-2 min-w-0">
                      <span className="truncate">
                        {selectedFile
                          ? selectedFile.name
                          : serverImageActive
                            ? mediaDisplayNameFromRef(
                                currentWaitingRoomPage.mainImageUrl ||
                                  currentWaitingRoomPage.mainImage,
                                t('waitingRoom.existingFile'),
                              )
                            : t('waitingRoom.selectFile')}
                      </span>
                      {(selectedFile || !serverImageActive) && (
                        <p className="text-sm text-gray-tertiary">
                          {selectedFile
                            ? formatFileSize(selectedFile.size, i18n.language)
                            : t('waitingRoom.noFileSelected')}
                        </p>
                      )}
                    </div>
                  </div>
                  {(selectedFile || serverImageActive) && (
                    <Trash2
                      className="cursor-pointer size-6 absolute top-1/2 -translate-y-1/2 right-6 shrink-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (selectedFile) {
                          setSelectedFile(null);
                          setShowImageReplacesVideoNote(false);
                        } else {
                          void removeServerMainMedia();
                        }
                      }}
                    />
                  )}
                </Label>
                {selectedFile && showImageReplacesVideoNote && (
                  <p className="text-sm text-amber-700 dark:text-amber-400 px-4 pb-3 pt-0 -mt-1">
                    {t('waitingRoom.imageReplacesVideoShort')}
                  </p>
                )}
              </div>
              <p>{t('waitingRoom.or')}</p>
              {/* Main video (upload-init + stockage + finalize) */}
              <div
                className="relative border w-full border-medium rounded-2xl"
                style={{ height: 'stretch' }}
              >
                <input
                  type="file"
                  id="video-upload-waiting-room"
                  className="sr-only"
                  accept="video/mp4,video/quicktime"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const maxBytes = 100 * 1024 * 1024;
                    const allowed = ['video/mp4', 'video/quicktime'];
                    if (file.size > maxBytes) {
                      toast.error(t('waitingRoom.videoTooLarge'));
                      e.target.value = '';
                      return;
                    }
                    if (!allowed.includes(file.type)) {
                      toast.error(t('waitingRoom.videoUnsupportedFormat'));
                      e.target.value = '';
                      return;
                    }
                    const replacesImage =
                      selectedFile != null ||
                      Boolean(
                        currentWaitingRoomPage &&
                        !mainImageCleared &&
                        currentWaitingRoomPage.mainAssetType !== 'VIDEO' &&
                        (currentWaitingRoomPage.mainImage?.trim() ||
                          currentWaitingRoomPage.mainImageUrl?.trim()),
                      );
                    setMainImageCleared(false);
                    setMainVideoCleared(false);
                    setShowImageReplacesVideoNote(false);
                    setSelectedFile(null);
                    setSelectedVideo(file);
                    setShowVideoReplacesImageNote(replacesImage);
                    e.target.value = '';
                  }}
                />
                <Label
                  htmlFor="video-upload-waiting-room"
                  className="relative p-4 h-auto bg-white rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1 pr-10">
                    <div className="size-10 shrink-0 bg-medium rounded-lg flex items-center justify-center">
                      <img
                        src="/assets//attach.svg"
                        alt={t('offersForm.content.fileInput.attachIconAlt')}
                      />
                    </div>
                    <div className="flex flex-col gap-2 min-w-0">
                      <span className="">
                        {selectedVideo
                          ? selectedVideo.name
                          : serverVideoActive
                            ? mediaDisplayNameFromRef(
                                currentWaitingRoomPage.mainVideoUrl ||
                                  currentWaitingRoomPage.mainVideo,
                                t('waitingRoom.existingVideo'),
                              )
                            : t('waitingRoom.selectVideo')}
                      </span>
                      {(selectedVideo || !serverVideoActive) && (
                        <p className="text-sm text-gray-tertiary">
                          {selectedVideo
                            ? formatFileSize(selectedVideo.size, i18n.language)
                            : t('waitingRoom.noVideoSelected')}
                        </p>
                      )}
                    </div>
                  </div>
                  {(selectedVideo || serverVideoActive) && (
                    <Trash2
                      className="cursor-pointer size-6 absolute top-1/2 -translate-y-1/2 right-6 shrink-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (selectedVideo) {
                          setSelectedVideo(null);
                          setShowVideoReplacesImageNote(false);
                        } else {
                          void removeServerMainMedia();
                        }
                      }}
                    />
                  )}
                </Label>
                {selectedVideo && showVideoReplacesImageNote && (
                  <p className="text-sm text-amber-700 dark:text-amber-400 px-4 pb-3 pt-0 -mt-1">
                    {t('waitingRoom.videoReplacesImageShort')}
                  </p>
                )}
              </div>
            </div>
            {/* Aperçu média principal : image ou vidéo */}
            {mainVideoPreviewSrc ? (
              <div className="flex justify-center items-center w-1/2 mx-auto rounded-2xl overflow-hidden">
                <video
                  src={mainVideoPreviewSrc}
                  className="w-full h-full object-contain max-h-[320px]"
                  controls
                  muted
                />
              </div>
            ) : null}
            {mainImagePreviewSrc ? (
              <div className="flex justify-center items-center w-1/2 mx-auto rounded-2xl overflow-hidden">
                <img src={mainImagePreviewSrc} alt="" className="w-full h-full object-cover" />
              </div>
            ) : null}
          </>
        )}
        <div className="flex justify-between items-center">
          <Label>{t('waitingRoom.showPreActivity')}</Label>
          <Switch
            checked={showPreWebinarActivity}
            onCheckedChange={(checked: boolean) => {
              if (checked) {
                setShowPreWebinarActivity(true);
                if (form.getValues('interactionType') === 'NONE') {
                  setSelectedActivity('SURVEY');
                  form.setValue('interactionType', 'SURVEY', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              } else {
                setShowPreWebinarActivity(false);
                form.setValue('interactionType', 'NONE', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setSelectedActivity('NONE');
              }
            }}
          />
        </div>
        {showPreWebinarActivity && (
          <>
            <Select
              value={selectedActivity}
              onValueChange={(value: WaitingRoomInteractionType) => {
                setSelectedActivity(value);
                form.setValue('interactionType', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                if (value === 'QUIZ' || value === 'SURVEY') {
                  setShowPreWebinarActivity(true);
                }
              }}
            >
              <SelectTrigger label={t('waitingRoom.activityTypeLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SURVEY">{t('waitingRoom.activitySurvey')}</SelectItem>
                <SelectItem value="QUIZ">{t('waitingRoom.activityQuiz')}</SelectItem>
              </SelectContent>
            </Select>

            {selectedActivity === 'SURVEY' && (
              <SurveyFormWaitingRoom
                webinarId={webinarId as number}
                workspaceId={currentWorkspace?.id as number}
                visualIdentity={visualIdentity!}
                showSurvey={showPreWebinarActivity}
                onFormReady={setSurveyForm}
                isEdit={isEdit}
                isEvergreen={isEvergreen}
              />
            )}

            {selectedActivity === 'QUIZ' && (
              <QuizFormWaitingRoom
                webinarId={webinarId as number}
                workspaceId={currentWorkspace?.id as number}
                visualIdentity={visualIdentity!}
                showQuiz={showPreWebinarActivity}
                onFormReady={setQuizForm}
                isEdit={isEdit}
                isEvergreen={isEvergreen}
              />
            )}
          </>
        )}
        <WaitingRoomBackground
          visualIdentity={visualIdentity!}
          form={form}
          onBackgroundChange={handleBackgroundChange}
          onGradientChange={handleGradientChange}
          onImageUpload={handleBackgroundImageSelect}
          backgroundImageUrl={
            backgroundImagePreviewUrl || currentWaitingRoomPage?.backgroundImageUrl
          }
          selectedBackgroundImageFileName={selectedBackgroundImageFile?.name ?? null}
          mainImageUrl={
            selectedFileUrl
              ? selectedFileUrl
              : mainImageCleared
                ? null
                : currentWaitingRoomPage?.mainImageUrl || null
          }
          mainImage={
            selectedFileUrl
              ? null
              : mainImageCleared
                ? null
                : currentWaitingRoomPage?.mainImage || null
          }
          mainVideo={mainVideoPreviewSrc || null}
          showMainMedia={showMainMedia}
          indexItems={currentWebinarIndex?.items}
          showPreWebinarActivity={showPreWebinarActivity}
          hasWaitingRoomQuiz={
            quizTemplates?.some(
              (quiz) =>
                quiz.webinarId ===
                  (typeof webinarId === 'number' ? webinarId : parseInt(webinarId as string, 10)) &&
                quiz.enabledDuringLive === false &&
                quiz.displayWaitingRoomQuiz === true,
            ) ||
            false ||
            Boolean(quizForm)
          }
          hasWaitingRoomSurvey={
            surveyTemplates?.some(
              (survey) =>
                survey.webinarId ===
                  (typeof webinarId === 'number' ? webinarId : parseInt(webinarId as string, 10)) &&
                survey.waitingRoomSurvey === true &&
                survey.displayWaitingRoomSurvey === true,
            ) ||
            false ||
            Boolean(surveyForm)
          }
          interactionType={form.watch('interactionType')}
          hasQuizFormData={
            quizTemplates?.some(
              (quiz) =>
                quiz.webinarId ===
                  (typeof webinarId === 'number' ? webinarId : parseInt(webinarId as string, 10)) &&
                quiz.enabledDuringLive === false &&
                quiz.displayWaitingRoomQuiz === true &&
                quiz.waitingRoomQuiz === true,
            ) ||
            false ||
            Boolean(quizForm)
          }
          hasSurveyFormData={
            surveyTemplates?.some(
              (survey) =>
                survey.webinarId ===
                  (typeof webinarId === 'number' ? webinarId : parseInt(webinarId as string, 10)) &&
                survey.waitingRoomSurvey === true &&
                survey.displayWaitingRoomSurvey === true,
            ) ||
            false ||
            Boolean(surveyForm)
          }
          quizForm={quizForm}
          surveyForm={surveyForm}
          isEdit={isEdit}
          isEvergreen={isEvergreen}
          onSubmit={onSubmit}
          currentWaitingRoomPage={currentWaitingRoomPage}
        />
        {isEdit && (
          <Button
            className="w-full"
            disabled={form.formState.isSubmitting}
            onClick={form.handleSubmit((data) => {
              void onSubmit(data);
            })}
          >
            {form.formState.isSubmitting && <Loader />} {tCW('save')}
          </Button>
        )}
      </div>
    </TitleLayout>
  );
}
